import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";

/* ═══════════════════════════════════════════
   THUNKS
═══════════════════════════════════════════ */

export const fetchManualOrders = createAsyncThunk(
  "manualOrders/fetchAll",
  async ({ page = 1, limit = 20, showSubmitted = false } = {}, thunkAPI) => {
    try {
      const res = await api.get(
        `/api/manualorders?page=${page}&limit=${limit}&status=${showSubmitted ? "submitted" : "draft"}`
      );
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to fetch manual orders"
      );
    }
  }
);

export const fetchManualOrderById = createAsyncThunk(
  "manualOrders/fetchById",
  async (id, thunkAPI) => {
    try {
      const res = await api.get(`/api/manualorders/${id}`);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to fetch manual order"
      );
    }
  }
);

export const createManualOrder = createAsyncThunk(
  "manualOrders/create",
  async (formData, thunkAPI) => {
    try {
      const res = await api.post("/api/manualorders", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to create manual order"
      );
    }
  }
);

export const updateManualOrder = createAsyncThunk(
  "manualOrders/update",
  async ({ id, formData }, thunkAPI) => {
    try {
      const res = await api.put(`/api/manualorders/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to update manual order"
      );
    }
  }
);

export const submitManualOrder = createAsyncThunk(
  "manualOrders/submit",
  async (id, thunkAPI) => {
    try {
      const res = await api.post(`/api/manualorders/${id}/submit`);
      return { id, order: res.data.data };
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to submit manual order"
      );
    }
  }
);

export const deleteManualOrder = createAsyncThunk(
  "manualOrders/delete",
  async (id, thunkAPI) => {
    try {
      await api.delete(`/api/manualorders/${id}`);
      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message || "Failed to delete manual order"
      );
    }
  }
);

export const lookupPinCode = createAsyncThunk(
  "manualOrders/pinLookup",
  async (pin, thunkAPI) => {
    try {
      const res = await api.get(`/api/manualorders/pincode/${pin}`);
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue("PIN lookup failed");
    }
  }
);

/* ═══════════════════════════════════════════
   SLICE
═══════════════════════════════════════════ */
const manualOrderSlice = createSlice({
  name: "manualOrders",
  initialState: {
    drafts:      [],
    current:     null,
    pagination:  {},
    loading:     false,
    saving:      false,
    submitting:  false,
    pinLoading:  false,
    pinData:     null,
    error:       null,
    successMsg:  null,
  },
  reducers: {
    clearManualOrderError:   (state) => { state.error      = null; },
    clearManualOrderSuccess: (state) => { state.successMsg = null; },
    clearCurrent:            (state) => { state.current    = null; },
    clearPinData:            (state) => { state.pinData    = null; },
  },
  extraReducers: (builder) => {
    builder

      /* FETCH ALL */
      .addCase(fetchManualOrders.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(fetchManualOrders.fulfilled, (state, action) => {
        state.loading    = false;
        state.drafts     = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchManualOrders.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      })

      /* FETCH SINGLE */
      .addCase(fetchManualOrderById.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(fetchManualOrderById.fulfilled, (state, action) => {
        state.loading = false; state.current = action.payload;
      })
      .addCase(fetchManualOrderById.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      })

      /* CREATE — no successMsg; only 💾 Save Draft button should toast */
      .addCase(createManualOrder.pending, (state) => {
        state.saving = true; state.error = null;
      })
      .addCase(createManualOrder.fulfilled, (state, action) => {
        state.saving  = false;
        state.current = action.payload;
        // ← no successMsg here — component sets it only when Save Draft is clicked
        state.drafts.unshift(action.payload);
      })
      .addCase(createManualOrder.rejected, (state, action) => {
        state.saving = false; state.error = action.payload;
      })

      /* UPDATE — no successMsg; only 💾 Save Draft button should toast */
      .addCase(updateManualOrder.pending, (state) => {
        state.saving = true; state.error = null;
      })
      .addCase(updateManualOrder.fulfilled, (state, action) => {
        state.saving  = false;
        state.current = action.payload;
        // ← no successMsg here — component sets it only when Save Draft is clicked
        const idx = state.drafts.findIndex((d) => d._id === action.payload._id);
        if (idx !== -1) state.drafts[idx] = action.payload;
      })
      .addCase(updateManualOrder.rejected, (state, action) => {
        state.saving = false; state.error = action.payload;
      })

      /* SUBMIT — only this sets successMsg */
      .addCase(submitManualOrder.pending, (state) => {
        state.submitting = true; state.error = null;
      })
      .addCase(submitManualOrder.fulfilled, (state, action) => {
        state.submitting = false;
        state.successMsg = "Order submitted to Order Received!";
        state.drafts = state.drafts.filter((d) => d._id !== action.payload.id);
        state.current = null;
      })
      .addCase(submitManualOrder.rejected, (state, action) => {
        state.submitting = false; state.error = action.payload;
      })

      /* DELETE */
      .addCase(deleteManualOrder.fulfilled, (state, action) => {
        state.drafts = state.drafts.filter((d) => d._id !== action.payload);
        state.successMsg = "Draft deleted";
      })
      .addCase(deleteManualOrder.rejected, (state, action) => {
        state.error = action.payload;
      })

      /* PIN LOOKUP */
      .addCase(lookupPinCode.pending, (state) => {
        state.pinLoading = true; state.pinData = null;
      })
      .addCase(lookupPinCode.fulfilled, (state, action) => {
        state.pinLoading = false;
        if (action.payload.success) {
          state.pinData = {
            district: action.payload.district,
            state:    action.payload.state,
          };
        }
      })
      .addCase(lookupPinCode.rejected, (state) => {
        state.pinLoading = false;
      });
  },
});

export const {
  clearManualOrderError,
  clearManualOrderSuccess,
  clearCurrent,
  clearPinData,
} = manualOrderSlice.actions;

export default manualOrderSlice.reducer;

/* ═══════════════════════════════════════════
   SELECTORS
═══════════════════════════════════════════ */
export const selectManualDrafts     = (state) => state.manualOrders.drafts;
export const selectCurrentDraft     = (state) => state.manualOrders.current;
export const selectManualPagination = (state) => state.manualOrders.pagination;
export const selectManualLoading    = (state) => state.manualOrders.loading;
export const selectManualSaving     = (state) => state.manualOrders.saving;
export const selectManualSubmitting = (state) => state.manualOrders.submitting;
export const selectPinData          = (state) => state.manualOrders.pinData;
export const selectPinLoading       = (state) => state.manualOrders.pinLoading;
export const selectManualError      = (state) => state.manualOrders.error;
export const selectManualSuccess    = (state) => state.manualOrders.successMsg;